import { ChevronDown, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { PicklistValue } from '@/api/client';
import type { BadgeKind } from '@/lib/cobalt-helpers';
import { cn } from '@/lib/utils';

// Opportunity status → badge colour. Synfo values: 0 Not Ready To Buy, 1 Active,
// 2 Lost, 3 Closed, 4 Implemented.
const STATUS_KIND: Record<number, BadgeKind> = {
  0: 'warning',
  1: 'brand',
  2: 'destructive',
  3: 'success',
  4: 'success',
};

interface Props {
  statusId: number | null;
  statusOpts: PicklistValue[];
  onSelect: (statusId: number) => void;
  pending?: boolean;
  size?: 'sm' | 'md';
}

/** Editable opportunity status — a coloured chip that opens a dropdown of status values. */
export function StatusPicker({ statusId, statusOpts, onSelect, pending, size = 'md' }: Props) {
  const current = statusOpts.find((s) => Number(s.value) === statusId);
  const label = current?.label ?? (statusId != null ? `#${statusId}` : 'No status');
  const kind = statusId != null ? (STATUS_KIND[statusId] ?? 'neutral') : 'neutral';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full font-medium leading-tight tracking-[0.01em]',
            'transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            kindClass(kind),
            size === 'sm' ? 'px-2 py-0.5 text-[11.5px]' : 'px-2.5 py-1 text-[12px]',
          )}
        >
          {pending ? (
            <Loader2 className="h-2.5 w-2.5 animate-spin shrink-0" />
          ) : (
            <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', dotClass(kind))} />
          )}
          <span>{label}</span>
          <ChevronDown className="h-2.5 w-2.5 shrink-0 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[180px]" onClick={(e) => e.stopPropagation()}>
        {statusOpts.map((opt) => {
          const optKind = STATUS_KIND[Number(opt.value)] ?? 'neutral';
          return (
            <DropdownMenuItem
              key={opt.value}
              onSelect={() => onSelect(Number(opt.value))}
              className={cn('flex items-center gap-2', Number(opt.value) === statusId && 'font-medium')}
            >
              <span className={cn('h-2 w-2 rounded-full shrink-0', dotClass(optKind))} />
              {opt.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function kindClass(k: BadgeKind): string {
  switch (k) {
    case 'brand':       return 'bg-brand-soft text-brand-strong dark:text-brand';
    case 'info':        return 'bg-info-soft text-info dark:text-info';
    case 'success':     return 'bg-success-soft text-success dark:text-success';
    case 'warning':     return 'bg-warning-soft text-warning dark:text-warning';
    case 'destructive': return 'bg-destructive-soft text-destructive dark:text-destructive';
    case 'neutral':
    default:            return 'bg-neutral-soft text-neutral dark:text-muted-foreground';
  }
}

function dotClass(k: BadgeKind): string {
  switch (k) {
    case 'brand':       return 'bg-brand dark:bg-brand';
    case 'info':        return 'bg-info';
    case 'success':     return 'bg-success';
    case 'warning':     return 'bg-warning';
    case 'destructive': return 'bg-destructive';
    case 'neutral':
    default:            return 'bg-neutral';
  }
}
